import React from "react";
import { motion } from "framer-motion";

const chapters = [
  {
    n: "01",
    title: "The ledger is the audit.",
    body:
      "Every commercial transaction in the physical economy needs a proof it happened. We collapse invoicing, provenance and audit into a single Merkle-anchored ledger — the record is the receipt.",
  },
  {
    n: "02",
    title: "IoT is the honest witness.",
    body:
      "Sensors don't negotiate. When a machine, pallet or meter signs an event, it enters a cryptographic chain that no ERP, controller or auditor can rewrite. We turn hardware into an immutable input to enterprise software.",
  },
  {
    n: "03",
    title: "Inter-ERP is the new EDI.",
    body:
      "SAP, Oracle, Microsoft Dynamics, Odoo — none of them talk to each other cleanly. Our layer sits between ERPs, translating documents into ledger-signed events. Automated commerce that survives the audit.",
  },
  {
    n: "04",
    title: "Convertible bonds fund it.",
    body:
      "Software IP is capital-hungry and cash-flow lean. We finance development through guarantee-backed convertible bonds — lenders get downside protection, upside in the IP. We keep the roadmap.",
  },
];

export const Manifesto = () => {
  return (
    <section
      id="manifesto"
      className="relative py-28 md:py-40 bg-ink-950"
      data-testid="manifesto-section"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-10">
        <div className="flex items-baseline justify-between border-b border-white/10 pb-6 mb-16">
          <div className="font-mono text-[11px] tracking-[0.35em] uppercase text-spectrum-green">
            ◢ Manifesto
          </div>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/40">
            04 chapters
          </div>
        </div>

        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white tracking-tighter leading-[0.95] mb-24 max-w-5xl"
          data-testid="manifesto-title"
        >
          A thesis in <span className="spectrum-text">four movements</span> for the ledger economy.
        </motion.h2>

        <div className="space-y-24 md:space-y-32">
          {chapters.map((c, i) => (
            <motion.article
              key={c.n}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, delay: 0.05 * i, ease: [0.22, 1, 0.36, 1] }}
              className="grid grid-cols-12 gap-6 md:gap-10 items-start border-t border-white/5 pt-10"
              data-testid={`manifesto-chapter-${c.n}`}
            >
              <div className="col-span-12 md:col-span-4 flex md:block items-baseline gap-6">
                <div className="font-display font-black text-[22vw] md:text-[10vw] leading-none tracking-tighter text-white/[0.06] select-none">
                  {c.n}
                </div>
                <div className="md:mt-4 font-mono text-[10px] uppercase tracking-[0.3em] text-spectrum-green">
                  Chapter {c.n}
                </div>
              </div>
              <div className="col-span-12 md:col-span-8">
                <h3 className="font-display text-3xl md:text-5xl font-bold text-white leading-[1.05] tracking-tight mb-6">
                  {c.title}
                </h3>
                <p className="text-white/60 text-base md:text-lg font-light max-w-2xl leading-relaxed">
                  {c.body}
                </p>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};
